import { useEffect, useMemo, useState } from 'react';
import { Marker, Source, Layer } from 'react-map-gl/maplibre';
import * as turf from '@turf/turf';
import { useFormContext } from 'react-hook-form';
import { HomeIcon } from '@heroicons/react/24/outline';
import { useAtomValue } from 'jotai';

import { MapWrapper } from '../../../../legacy';
import { cn, defaultItalyCoordinates } from '@restworld/utils-common';

import { SaveResearchFormFields, saveResearchMapRefAtom } from '../job-explorer-save-research-v2';

const defaultZoom = 10;
const defaultKM = 3;
export const SaveResearchMap = () => {
  const mapRef = useAtomValue(saveResearchMapRefAtom);

  const { getValues } = useFormContext<SaveResearchFormFields>();
  const [centerPoint, setCenterPoint] = useState<[number, number]>(defaultItalyCoordinates);
  const [markerPoint, setMarkerPoint] = useState<[number, number]>();
  const [zoomLevel, handleZoomEnd] = useState(defaultZoom);

  const address = getValues('address');
  const areaDistance = getValues('area_distance') ?? defaultKM;

  const initialViewState = useMemo(
    () => ({
      longitude: centerPoint?.[0],
      latitude: centerPoint?.[1],
      zoom: 5,
    }),
    [centerPoint]
  );

  const buffered = useMemo(() => {
    if (!markerPoint) return;
    try {
      const pointer = turf.point(markerPoint);
      return turf.buffer(pointer, areaDistance, { units: 'kilometers' });
    } catch (error) {
      console.error(error, `error on creating circular buffer`);
    }
  }, [areaDistance, markerPoint]);

  useEffect(() => {
    if (address?.address) {
      try {
        if (address.address.split(' ').length === 1) handleZoomEnd(5);
        else handleZoomEnd(defaultZoom);
        if (address.address_lon) {
          setMarkerPoint([address.address_lon, address.address_lat]);
          setCenterPoint([address.address_lon, address.address_lat]);
        }
      } catch (error) {
        console.error(error, `error centering and zooming`);
      }
    }
  }, [address]);

  useEffect(() => {
    mapRef.current?.flyTo({
      center: centerPoint,
      zoom: zoomLevel,
      duration: 500,
    });
  }, [centerPoint, mapRef, zoomLevel]);

  return (
    <Wrapper data-cy="save-research-map" id="map-wrapper">
      <MapWrapper
        cooperativeGestures={false}
        initialViewState={initialViewState}
        zoom={zoomLevel}
        mapRef={mapRef}
        touchZoomRotate
        coordinates={centerPoint}
      >
        {markerPoint && (
          <Marker longitude={markerPoint?.[0]} latitude={markerPoint?.[1]} style={{ position: 'relative' }}>
            <HomePin>
              <HomeIcon className="h-[18px] w-[18px] color-black" />
            </HomePin>
          </Marker>
        )}
        {/* Only render the radius source once the buffer geometry exists — MapLibre errors
            ("sources.circular-radius: missing required property data") on data={undefined}. */}
        {buffered && (
          <Source id="circular-radius" type="geojson" data={buffered}>
            <Layer
              id="point-90-hi"
              type="fill"
              paint={{
                'fill-color': '#693eb5',
                'fill-opacity': 0.15,
              }}
            />
          </Source>
        )}
      </MapWrapper>
    </Wrapper>
  );
};
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'relative w-full h-[150px] rounded-[4px] [&_.maplibregl-map]:h-full [&_.maplibregl-map]:w-full',
      className
    )}
    {...props}
  />
);
const HomePin = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex justify-center items-center bg-white w-[35px] h-[35px] rounded-full',
      "after:content-[''] after:absolute after:-bottom-[10px] after:left-1/2 after:ml-0 after:border-[6px] after:border-solid after:[border-color:white_transparent_transparent_transparent] after:rounded-t-[50%] after:-translate-x-1/2",
      className
    )}
    {...props}
  />
);
